import { colors, intl, ui } from '@beelday/common'
import styled from '@emotion/styled'
import { ChangeEvent, FC } from 'react'
import { DEFAULT_SINK, SpeakersView } from '../model/speakers'

const Block = styled.section`
	display: flex;
	flex-direction: column;
	gap: 6px;
	flex: none;
`

const Caption = styled.label`
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: ${colors.heather};
`

const Select = styled.select`
	width: 100%;
	padding: 8px 10px;
	border: 1px solid ${colors.borderGray};
	border-radius: 10px;
	background: ${colors.white};
	font-family: inherit;
	font-size: 14px;
	color: ${colors.darkIndigo};
	cursor: pointer;

	:focus-visible {
		outline: 2px solid ${colors.indigoBlue};
		outline-offset: 1px;
	}
`

const Note = styled.p`
	margin: 0;
	font-size: 13px;
	line-height: 1.4;
	color: ${colors.heather};
`

/** A live region: announces the warning when it appears. Empty, it takes no room. */
const Live = styled.div`
	:empty {
		margin-top: -6px;
	}
`

const Warning = styled.p`
	margin: 0;
	padding: 8px 10px;
	border-radius: 10px;
	background: ${colors.gold15};
	box-shadow: inset 3px 0 0 ${colors.dustyOrange};
	font-size: 13px;
	font-weight: 600;
	line-height: 1.4;
	color: ${colors.darkIndigo};
`

type Props = {
	view: SpeakersView
	/** `null` is the system default. */
	onChoose: (deviceId: string | null) => void
	/** Asks the browser for audio access, which reveals the devices' names. */
	onRevealNames: () => void
}

/**
 * Which speakers the training's sound comes out of: the laptop's, a room
 * speaker, the projector's HDMI audio. Recordings follow the choice; YouTube
 * films cannot (they play inside a YouTube frame, always on the system
 * default), which the picker says as long as speakers are chosen.
 */
export const SpeakersPicker: FC<Props> = ({
	view,
	onChoose,
	onRevealNames,
}) => {
	const translate = intl.useTranslate()
	const selectId = 'presenter-speakers'

	if (view.kind === 'unsupported') {
		return (
			<Block data-testid="presenter.speakers">
				<Caption as="h3">
					<intl.Translate>in_person.presenter.speakers</intl.Translate>
				</Caption>
				<Note data-testid="presenter.speakers.unsupported">
					<intl.Translate>
						in_person.presenter.speakers.unsupported
					</intl.Translate>
				</Note>
			</Block>
		)
	}

	const choose = (event: ChangeEvent<HTMLSelectElement>): void =>
		onChoose(event.target.value === DEFAULT_SINK ? null : event.target.value)

	return (
		<Block data-testid="presenter.speakers">
			<Caption htmlFor={selectId}>
				<intl.Translate>in_person.presenter.speakers</intl.Translate>
			</Caption>
			<Select
				id={selectId}
				data-testid="presenter.speakers.select"
				value={view.selected}
				onChange={choose}
			>
				<option value={DEFAULT_SINK}>
					{translate('in_person.presenter.speakers.default')}
				</option>
				{view.outputs.map(output => (
					<option key={output.deviceId} value={output.deviceId}>
						{output.label}
					</option>
				))}
			</Select>
			{view.namesHidden ? (
				<ui.ButtonMinor
					small
					onClick={onRevealNames}
					data-testid="presenter.speakers.reveal"
				>
					<intl.Translate>
						in_person.presenter.speakers.show_names
					</intl.Translate>
				</ui.ButtonMinor>
			) : null}
			<Live role="status" data-testid="presenter.speakers.status">
				{view.unavailable ? (
					<Warning data-testid="presenter.speakers.unavailable">
						<intl.Translate>
							in_person.presenter.speakers.unavailable
						</intl.Translate>
					</Warning>
				) : null}
			</Live>
			{view.youtubeNote ? (
				<Note data-testid="presenter.speakers.youtube_note">
					<intl.Translate>in_person.presenter.speakers.youtube</intl.Translate>
				</Note>
			) : null}
		</Block>
	)
}
